import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { 
  LayoutDashboard, 
  Landmark, 
  FileInput, 
  FileOutput, 
  Receipt, 
  Users, 
  Briefcase, 
  RotateCcw, 
  TrendingUp,
  Settings,
  LogOut,
  User
} from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const primaryNavItems = [
  { path: "/cashflow", label: "Cashflow", icon: LayoutDashboard },
  { path: "/bank-balance", label: "Bank Balance", icon: Landmark },
  { path: "/purchase-invoices", label: "Purchase Invoices", icon: FileInput },
  { path: "/sales-invoices", label: "Sales Invoices", icon: FileOutput },
  { path: "/taxes", label: "Taxes", icon: Receipt },
  { path: "/salaries", label: "Salaries", icon: Users },
  { path: "/management-fees", label: "Management Fees", icon: Briefcase },
  { path: "/repayments", label: "Repayments", icon: RotateCcw },
  { path: "/revenue-forecast", label: "Revenue Forecast", icon: TrendingUp },
];

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <nav className="fixed left-0 top-0 h-full w-64 bg-white border-r border-border flex flex-col z-40" data-testid="main-navigation">
      {/* Logo */}
      <div className="p-6 border-b border-border">
        <h1 className="text-xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
          Floatly
        </h1>
        <p className="text-xs text-muted-foreground mt-1">Cash forecasting</p>
      </div>

      {/* Primary Navigation */}
      <div className="flex-1 overflow-y-auto py-4">
        <div className="px-3 mb-2">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3">
            Main Menu
          </span>
        </div>
        <ul className="space-y-1 px-3">
          {primaryNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
              (item.path === "/cashflow" && location.pathname === "/");
            
            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                    isActive 
                      ? "bg-primary text-primary-foreground" 
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Settings Link */}
      <div className="border-t border-border py-4">
        <ul className="space-y-1 px-3">
          <li>
            <NavLink
              to="/settings"
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                location.pathname === "/settings"
                  ? "bg-muted text-foreground" 
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
              data-testid="nav-settings"
            >
              <Settings className="w-4 h-4" />
              Settings
            </NavLink>
          </li>
        </ul>
      </div>

      {/* User Menu */}
      {user && (
        <div className="border-t border-border p-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start gap-3 h-auto py-2" data-testid="user-menu-button">
                {user.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name} 
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                )}
                <div className="flex-1 text-left overflow-hidden">
                  <p className="text-sm font-medium truncate">{user.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate("/settings#account")} data-testid="menu-account">
                <User className="w-4 h-4 mr-2" />
                Account Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
